import { useEffect } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'
import Header from 'components/headers/addLoan'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'
import Requested from './tabs/requested'
import Accepted from './tabs/accepted'
import Rejected from './tabs/rejected'

type TTab = 'requested' | 'accepted' | 'rejected'

const TABS: { key: TTab; label: string }[] = [
    { key: 'requested', label: 'در انتظار بررسی' },
    { key: 'accepted', label: 'پذیرفته‌شده' },
    { key: 'rejected', label: 'رد یا لغوشده' },
]

/**
 * «وام‌های درخواستی» — the fund's loan-request queue.
 *
 * Rebuilt 2026-09-11 («هنوز ری‌دیزاین نشده و هنوز زشته») in the language of
 * the screens around it — the کارتابل, the request and grant wizards,
 * «درخواست‌های برداشت» — instead of the green react-bootstrap tab bar and the
 * accordion rows it had since the first PWA.
 *
 * What changed besides the look:
 *
 * - **A row opens its کارتابل.** The accordion stays for the member's numbers,
 *   but the request itself — its thread, the managers' votes, the decision —
 *   lives on one page now, and the list is the way into it.
 * - **The role is read from the fund,** not only from router state. The
 *   کارتابل links back here without state, and a manager arriving that way was
 *   shown the member's view: no sort, no filter, no votes.
 * - **The tab is in `?tab=`,** so coming back from a کارتابل lands on the tab
 *   you left rather than on «در انتظار» every time.
 */
export default function RequestedLoans() {
    const { id } = useParams()
    const { state } = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()

    const { moneyPoolManagement } = useGetMoneyPoolManagement(id)
    const role = moneyPoolManagement?.poolship?.role
    const isManager = role
        ? role === 'mgr' || role === 'own'
        : !!state?.isManager

    const tab = (TABS.find((item) => item.key === searchParams.get('tab'))
        ?.key ?? 'requested') as TTab

    const choose = (next: TTab) => {
        if (next === tab) return
        const params = new URLSearchParams(searchParams)
        params.set('tab', next)
        // replace: a tab is a view of this page, not a place — back should
        // leave the queue, not walk through every tab you glanced at
        setSearchParams(params, { replace: true, state })
    }

    useEffect(() => {
        posthog.capture('loan_demands_tab_viewed', {
            fund_id: id,
            tab,
            is_manager: isManager,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, id])

    return (
        <div className=" min-h-screen bg-canvas pb-28">
            <Header title="وام‌های درخواستی" />

            <div className=" sticky top-0 z-10 bg-canvas px-4 pb-2 pt-1">
                <div
                    role="tablist"
                    aria-label="وضعیت درخواست‌ها"
                    className=" flex rounded-full border border-stroke bg-white p-1"
                >
                    {TABS.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            role="tab"
                            aria-selected={tab === item.key}
                            onClick={() => choose(item.key)}
                            className={` flex-1 rounded-full py-2 text-[12px] font-bold transition-colors ${
                                tab === item.key
                                    ? ' bg-green-1 text-white'
                                    : ' text-text-secondary'
                            }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className=" px-4" role="tabpanel">
                {tab === 'requested' && <Requested isManager={isManager} />}
                {tab === 'accepted' && <Accepted />}
                {tab === 'rejected' && <Rejected />}
            </div>
        </div>
    )
}
